import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaBook, FaFlask, FaSyncAlt, FaPlus, FaTrash, FaToggleOn, FaToggleOff, FaPlay, FaHammer } from 'react-icons/fa';
import './AssistantRules.css';

// Structured rules with provenance and the evaluation harness of an assistant
// (backend plan E2): rules are rendered into the agent's context by id and
// scope; the harness replays the reference cases (built from the account's
// corrections) and gates instruction changes.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const pct = (value) => (typeof value === 'number' ? `${Math.round(value * 100)} %` : '—');
const splitList = (text) => (text || '').split(',').map((s) => s.trim()).filter(Boolean);

const AssistantRules = ({ assistantId }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [rules, setRules] = useState(null);
    const [cases, setCases] = useState(null);
    const [reports, setReports] = useState([]);
    const [activeReport, setActiveReport] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ text: '', carriers: '', topics: '', channels: '', note: '', run_id: '' });
    const pollRef = useRef(null);

    const load = useCallback(async () => {
        if (!assistantId) return;
        setError(null);
        try {
            const [rulesRes, casesRes, reportsRes] = await Promise.all([
                axios.get(`/api/ai-eval/rules?assistant_id=${encodeURIComponent(assistantId)}`, authHeaders()),
                axios.get(`/api/ai-eval/cases?assistant_id=${encodeURIComponent(assistantId)}&limit=200`, authHeaders()),
                axios.get(`/api/ai-eval/runs?assistant_id=${encodeURIComponent(assistantId)}&limit=5`, authHeaders()),
            ]);
            setRules(rulesRes.data.rules || []);
            setCases(casesRes.data.cases || []);
            setReports(reportsRes.data.reports || []);
        } catch (e) {
            setRules([]);
            setCases([]);
            setError(t('assistantRules.loadError', 'Impossible de charger les règles et le harnais.'));
        }
    }, [assistantId, t]);

    useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [load]);

    const fail = (e, fallback) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : fallback);
    };

    const addRule = async () => {
        if (!form.text.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await axios.post('/api/ai-eval/rules', {
                assistant_id: assistantId, text: form.text.trim(),
                scope: { carriers: splitList(form.carriers), topics: splitList(form.topics), channels: splitList(form.channels) },
                note: form.note || null, run_id: form.run_id.trim() || null, source: form.run_id.trim() ? 'incident' : 'manual',
            }, authHeaders());
            setForm({ text: '', carriers: '', topics: '', channels: '', note: '', run_id: '' });
            await load();
        } catch (e) {
            fail(e, t('assistantRules.saveError', 'La règle n’a pas pu être enregistrée.'));
        } finally {
            setBusy(false);
        }
    };

    const toggleRule = async (rule) => {
        try {
            await axios.put(`/api/ai-eval/rules/${rule.id}`, { active: !rule.active }, authHeaders());
            await load();
        } catch (e) {
            fail(e, t('assistantRules.saveError', 'La règle n’a pas pu être enregistrée.'));
        }
    };

    const removeRule = async (rule) => {
        if (!window.confirm(t('assistantRules.confirmDelete', 'Supprimer la règle {{id}} ?', { id: rule.rule_id }))) return;
        try {
            await axios.delete(`/api/ai-eval/rules/${rule.id}`, authHeaders());
            await load();
        } catch (e) {
            fail(e, t('assistantRules.saveError', 'La règle n’a pas pu être enregistrée.'));
        }
    };

    const buildCases = async () => {
        setBusy(true);
        setError(null);
        try {
            await axios.post('/api/ai-eval/cases/build', { assistant_id: assistantId, since_days: 90 }, authHeaders());
            await load();
        } catch (e) {
            fail(e, t('assistantRules.buildError', 'Impossible de construire le jeu de référence.'));
        } finally {
            setBusy(false);
        }
    };

    const runHarness = async () => {
        setBusy(true);
        setError(null);
        try {
            const response = await axios.post('/api/ai-eval/runs', { assistant_id: assistantId, mode: 'call', judge: false }, authHeaders());
            const id = response.data.report.id;
            setActiveReport(response.data.report);
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(async () => {
                try {
                    const poll = await axios.get(`/api/ai-eval/runs/${id}`, authHeaders());
                    setActiveReport(poll.data.report);
                    if (['done', 'failed'].includes(poll.data.report.status)) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        await load();
                    }
                } catch (e) { /* keep polling */ }
            }, 3000);
        } catch (e) {
            fail(e, t('assistantRules.runError', 'Le harnais n’a pas pu être lancé.'));
        } finally {
            setBusy(false);
        }
    };

    const scopeChips = (scope) => Object.entries(scope || {}).flatMap(([key, values]) => (values || []).map((v) => (
        <span key={`${key}-${v}`} className="asr-chip" title={key}>{v}</span>
    )));

    const lastReport = activeReport || reports[0] || null;
    const failedEntries = (lastReport?.entries || []).filter((e) => e.passed === false && !e.skipped);

    return (
        <section className="assistant-rules">
            <div className="asr-header">
                <h3><FaBook /> {t('assistantRules.title', 'Règles structurées')} {rules && <span className="asr-muted">({rules.length})</span>}</h3>
                <button type="button" className="asr-icon" onClick={load} title={t('assistantRules.refresh', 'Actualiser')}><FaSyncAlt /></button>
            </div>
            <p className="asr-intro">{t('assistantRules.intro', 'Chaque règle a un identifiant, une portée (transporteurs, sujets, canaux) et une provenance ; les règles actives sont données à l’assistant pour les tickets qu’elles concernent.')}</p>
            {error && <div className="asr-error">{error}</div>}
            {rules && rules.length === 0 && <div className="asr-muted">{t('assistantRules.empty', 'Aucune règle : les consignes vivent encore uniquement dans le texte des instructions.')}</div>}
            {rules && rules.length > 0 && (
                <div className="asr-list">
                    {rules.map((rule) => (
                        <div key={rule.id} className={`asr-rule ${rule.active ? '' : 'inactive'}`}>
                            <div className="asr-rule-head">
                                <span className="asr-rule-id">{rule.rule_id}</span>
                                <span className="asr-rule-scope">{scopeChips(rule.scope)}{!Object.keys(rule.scope || {}).length && <span className="asr-muted">{t('assistantRules.allTickets', 'tous les tickets')}</span>}</span>
                                <span className="asr-rule-actions">
                                    <button type="button" className="asr-icon" onClick={() => toggleRule(rule)} title={rule.active ? t('assistantRules.deactivate', 'Désactiver') : t('assistantRules.activate', 'Activer')}>
                                        {rule.active ? <FaToggleOn /> : <FaToggleOff />}
                                    </button>
                                    <button type="button" className="asr-icon" onClick={() => removeRule(rule)} title={t('assistantRules.delete', 'Supprimer')}><FaTrash /></button>
                                </span>
                            </div>
                            <div className="asr-rule-text">{rule.text}</div>
                            <div className="asr-rule-meta">
                                {t('assistantRules.provenance', 'provenance')} : {rule.provenance?.source}
                                {rule.provenance?.ticket_id && <> · <a href={`/tickets/${rule.provenance.ticket_id}`} target="_blank" rel="noopener noreferrer">ticket</a></>}
                                {rule.provenance?.run_id && <> · {t('assistantRules.run', 'run')} {String(rule.provenance.run_id).slice(0, 12)}</>}
                                {rule.version > 1 && <> · v{rule.version}</>}
                                {rule.eval_case_id && <> · {t('assistantRules.hasCase', 'cas de référence lié')}</>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="asr-form">
                <textarea className="asr-input" rows={2} placeholder={t('assistantRules.textPlaceholder', 'Nouvelle règle (une consigne précise, vérifiable)')} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
                <div className="asr-form-row">
                    <input className="asr-input" placeholder={t('assistantRules.carriers', 'Transporteurs (virgule)')} value={form.carriers} onChange={(e) => setForm({ ...form, carriers: e.target.value })} />
                    <input className="asr-input" placeholder={t('assistantRules.topics', 'Sujets (virgule)')} value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} />
                    <input className="asr-input" placeholder={t('assistantRules.channels', 'Canaux (virgule)')} value={form.channels} onChange={(e) => setForm({ ...form, channels: e.target.value })} />
                </div>
                <div className="asr-form-row">
                    <input className="asr-input" placeholder={t('assistantRules.runId', 'Run d’origine (optionnel : crée le cas de référence)')} value={form.run_id} onChange={(e) => setForm({ ...form, run_id: e.target.value })} />
                    <input className="asr-input" placeholder={t('assistantRules.note', 'Note de provenance')} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    <button type="button" className="asr-btn" onClick={addRule} disabled={busy || !form.text.trim()}><FaPlus /> {t('assistantRules.add', 'Ajouter')}</button>
                </div>
            </div>

            <div className="asr-header asr-harness-header">
                <h3><FaFlask /> {t('assistantRules.harnessTitle', 'Harnais d’évaluation')} {cases && <span className="asr-muted">({cases.length} {t('assistantRules.cases', 'cas')})</span>}</h3>
                <span className="asr-rule-actions">
                    <button type="button" className="asr-btn" onClick={buildCases} disabled={busy}><FaHammer /> {t('assistantRules.build', 'Construire depuis les corrections')}</button>
                    <button type="button" className="asr-btn asr-btn-primary" onClick={runHarness} disabled={busy || !cases || cases.length === 0}><FaPlay /> {t('assistantRules.run', 'Lancer')}</button>
                </span>
            </div>
            <p className="asr-intro">{t('assistantRules.harnessIntro', 'Les brouillons approuvés ou corrigés, les messages transporteur validés et les statuts repris deviennent des cas de référence ; le harnais les rejoue et vérifie outils, statut, absence d’action annoncée sans outil et dérive du texte. Un changement d’instructions doit passer ce jeu avant d’être appliqué.')}</p>
            {lastReport && (
                <div className={`asr-report asr-report-${lastReport.status}`}>
                    <div className="asr-report-head">
                        <strong>{t(`assistantRules.reportStatus.${lastReport.status}`, lastReport.status)}</strong>
                        {lastReport.score && (
                            <span> · {t('assistantRules.passRate', 'taux de réussite')} {pct(lastReport.score.pass_rate)} ({lastReport.score.passed}/{lastReport.score.replayed}) · {t('assistantRules.gate', 'barrière')} {lastReport.score.gate_passed ? '✓' : '✗'} ({t('assistantRules.min', 'min.')} {pct(lastReport.score.min_pass)})</span>
                        )}
                        {lastReport.created_at && <span className="asr-muted"> · {new Date(lastReport.created_at).toLocaleString(lang)}</span>}
                    </div>
                    {lastReport.score?.failed_checks && Object.keys(lastReport.score.failed_checks).length > 0 && (
                        <div className="asr-report-checks">
                            {Object.entries(lastReport.score.failed_checks).map(([name, count]) => (
                                <span key={name} className="asr-chip asr-chip-bad">{t(`assistantRules.check.${name}`, name.replace(/_/g, ' '))} × {count}</span>
                            ))}
                        </div>
                    )}
                    {lastReport.error && <div className="asr-error">{lastReport.error}</div>}
                    {failedEntries.length > 0 && (
                        <div className="asr-failed">
                            {failedEntries.slice(0, 10).map((e) => (
                                <div key={e.case_id} className="asr-failed-row">
                                    <span className="asr-chip">{t(`assistantRules.kind.${e.kind}`, e.kind)}</span>
                                    {e.ticket_id && <a href={`/tickets/${e.ticket_id}`} target="_blank" rel="noopener noreferrer">ticket</a>}
                                    <span className="asr-muted">{(e.checks || []).filter((c) => c.passed === false).map((c) => t(`assistantRules.check.${c.name}`, c.name)).join(', ')}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default AssistantRules;
